import { useCallback, useState } from 'react';

import { generateKey } from 'utilities/Keys';
import { createSchemaField, insertSchemaFieldAtVisibleIndex } from 'utilities/NodeSchemaFields';
import {
  addNodeTypeField,
  deleteNodeTypeField,
  updateNodeTypeField,
} from 'services/nodetypes/NodeTypeMutationService';
import { FIELD_LOG_MESSAGES, FIELD_MESSAGES } from 'node/form/constants/fieldMessages';
import { ONCE_ONLY_FIELD_TYPES } from 'node/form/fieldmodal/constants';

export default function useNodeTypeFieldMutations({
  isDisabled = false,
  fieldEditingField,
  fieldInsertIndex,
  fields,
  resolvedFields,
  overrideFields,
  setOverrideFields,
  resolvedTypeKey,
  resolvedWorkspaceKey,
  getNodeKey,
  requireNodeKey = false,
  nodeNotReadyMessage = FIELD_MESSAGES.NODE_NOT_READY,
  typeNotReadyMessage = FIELD_MESSAGES.NODE_TYPE_NOT_READY,
  refreshNodeTypesCache,
  closeFieldModal,
  applyDefaultToNode,
}) {
  const [fieldSaving, setFieldSaving] = useState(false);

  const handleFieldSave = useCallback(async (fieldDraft) => {
    if (!fieldDraft?.label || !fieldDraft?.type || fieldSaving || isDisabled) return;

    const nodeKey = typeof getNodeKey === 'function' ? getNodeKey() : null;
    if (requireNodeKey && !nodeKey) {
      alert(nodeNotReadyMessage);
      return;
    }
    if (!resolvedTypeKey) {
      alert(typeNotReadyMessage);
      return;
    }

    const existingFieldWithType = resolvedFields.find((field) => field?.type === fieldDraft.type);
    if (ONCE_ONLY_FIELD_TYPES.includes(fieldDraft.type) && existingFieldWithType) {
      alert(`${fieldDraft.label || 'This field'} can only be added once for this type.`);
      return;
    }

    const nextField = createSchemaField({
      name: generateKey(),
      label: fieldDraft.label,
      fieldDescription: fieldDraft.fieldDescription,
      type: fieldDraft.type,
      targetType: fieldDraft.targetType,
      defaultValue: fieldDraft.default,
      defaultUnit: fieldDraft.defaultUnit,
      options: fieldDraft.options,
      enumOptionsSort: fieldDraft.enumOptionsSort,
      source: resolvedTypeKey,
      inherited: false,
    });

    const insertBeforeName =
      typeof fieldInsertIndex === 'number' && fieldInsertIndex < fields.length
        ? fields[fieldInsertIndex]?.name
        : null;

    setFieldSaving(true);
    try {
      await addNodeTypeField({
        nodeKey: nodeKey || undefined,
        typeKey: resolvedTypeKey,
        workspaceKey: resolvedWorkspaceKey,
        field: nextField,
        insertBeforeName,
      });

      const baseFields = Array.isArray(overrideFields) ? overrideFields : resolvedFields;
      const nextFields = insertSchemaFieldAtVisibleIndex(baseFields, fields, fieldInsertIndex, nextField);
      setOverrideFields(nextFields);

      await refreshNodeTypesCache?.({
        workspaceKey: resolvedWorkspaceKey,
        typeKey: resolvedTypeKey,
        fields: nextFields,
      });
      applyDefaultToNode?.(nextField, fieldDraft.default);
      closeFieldModal?.();
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.ADD_FIELD_FAILED, error);
      alert(error?.message || FIELD_MESSAGES.ADD_FAILED);
    } finally {
      setFieldSaving(false);
    }
  }, [
    applyDefaultToNode,
    closeFieldModal,
    fields,
    getNodeKey,
    isDisabled,
    nodeNotReadyMessage,
    overrideFields,
    fieldInsertIndex,
    fieldSaving,
    refreshNodeTypesCache,
    requireNodeKey,
    resolvedFields,
    resolvedTypeKey,
    resolvedWorkspaceKey,
    setOverrideFields,
    typeNotReadyMessage,
  ]);

  const handleFieldUpdate = useCallback(async (fieldDraft) => {
    if (!fieldDraft?.label || !fieldDraft?.type || fieldSaving || isDisabled) return;
    if (!fieldEditingField?.name) return;

    const nodeKey = typeof getNodeKey === 'function' ? getNodeKey() : null;
    if (requireNodeKey && !nodeKey) {
      alert(nodeNotReadyMessage);
      return;
    }
    if (!resolvedTypeKey) {
      alert(typeNotReadyMessage);
      return;
    }

    const nextField = {
      ...fieldEditingField,
      label: fieldDraft.label,
      fieldDescription: String(fieldDraft.fieldDescription || '').trim(),
      type: fieldEditingField.type,
      default: fieldDraft.default,
      options: fieldEditingField.type === 'enum' || fieldEditingField.type === 'multiselect' || fieldEditingField.type === 'measurement'
        ? fieldDraft.options
        : fieldEditingField.options,
      defaultUnit: fieldEditingField.type === 'measurement'
        ? fieldDraft.defaultUnit
        : fieldEditingField.defaultUnit,
      enumOptionsSort: fieldEditingField.type === 'enum'
        ? fieldDraft.enumOptionsSort
        : fieldEditingField.enumOptionsSort,
    };

    if (fieldEditingField.type === 'checklist') {
      delete nextField.templateKeys;
    }

    setFieldSaving(true);
    try {
      await updateNodeTypeField({
        nodeKey: nodeKey || undefined,
        typeKey: resolvedTypeKey,
        workspaceKey: resolvedWorkspaceKey,
        field: nextField,
      });

      const baseFields = Array.isArray(overrideFields) ? overrideFields : resolvedFields;
      const patch = {
        label: nextField.label,
        fieldDescription: nextField.fieldDescription,
        default: nextField.default,
      };
      if (fieldEditingField.type === 'enum' || fieldEditingField.type === 'multiselect' || fieldEditingField.type === 'measurement') {
        patch.options = nextField.options;
      }
      if (fieldEditingField.type === 'measurement') {
        patch.defaultUnit = nextField.defaultUnit;
      }
      if (fieldEditingField.type === 'enum') {
        patch.enumOptionsSort = nextField.enumOptionsSort;
      }

      const nextFields = baseFields.map((field) => {
        if (field?.name !== fieldEditingField.name) {
          return field;
        }

        const nextPatchedField = { ...field, ...patch };
        if (fieldEditingField.type === 'checklist') {
          delete nextPatchedField.templateKeys;
        }

        return nextPatchedField;
      });
      setOverrideFields(nextFields);

      await refreshNodeTypesCache?.({
        workspaceKey: resolvedWorkspaceKey,
        typeKey: resolvedTypeKey,
        fields: nextFields,
      });
      closeFieldModal?.();
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.UPDATE_FIELD_FAILED, error);
      alert(error?.message || FIELD_MESSAGES.UPDATE_FAILED);
    } finally {
      setFieldSaving(false);
    }
  }, [
    closeFieldModal,
    getNodeKey,
    isDisabled,
    nodeNotReadyMessage,
    overrideFields,
    fieldEditingField,
    fieldSaving,
    refreshNodeTypesCache,
    requireNodeKey,
    resolvedFields,
    resolvedTypeKey,
    resolvedWorkspaceKey,
    setOverrideFields,
    typeNotReadyMessage,
  ]);

  const handleFieldDelete = useCallback(async () => {
    if (fieldSaving || isDisabled) return;
    if (!fieldEditingField?.name) return;
    if (fieldEditingField?.inherited) return;

    const nodeKey = typeof getNodeKey === 'function' ? getNodeKey() : null;
    if (requireNodeKey && !nodeKey) {
      alert(nodeNotReadyMessage);
      return;
    }
    if (!resolvedTypeKey) {
      alert(typeNotReadyMessage);
      return;
    }

    setFieldSaving(true);
    try {
      await deleteNodeTypeField({
        nodeKey: nodeKey || undefined,
        typeKey: resolvedTypeKey,
        workspaceKey: resolvedWorkspaceKey,
        fieldName: fieldEditingField.name,
      });

      const baseFields = Array.isArray(overrideFields) ? overrideFields : resolvedFields;
      const nextFields = baseFields.filter((field) => field?.name !== fieldEditingField.name);
      setOverrideFields(nextFields);

      await refreshNodeTypesCache?.({
        workspaceKey: resolvedWorkspaceKey,
        typeKey: resolvedTypeKey,
        fields: nextFields,
      });
      closeFieldModal?.();
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.DELETE_FIELD_FAILED, error);
      alert(error?.message || FIELD_MESSAGES.DELETE_FAILED);
    } finally {
      setFieldSaving(false);
    }
  }, [
    closeFieldModal,
    getNodeKey,
    isDisabled,
    nodeNotReadyMessage,
    overrideFields,
    fieldEditingField,
    fieldSaving,
    refreshNodeTypesCache,
    requireNodeKey,
    resolvedFields,
    resolvedTypeKey,
    resolvedWorkspaceKey,
    setOverrideFields,
    typeNotReadyMessage,
  ]);

  return {
    fieldSaving,
    handleFieldSave,
    handleFieldUpdate,
    handleFieldDelete,
  };
}